import { useMemo } from 'react';

import { MenuObject } from '@rbi-ctg/menu';
import { useAcceleratorBadgesOnMenuExperiment } from 'experiments/use-accelerator-badges-on-menu-experiment';
import { useSystemwidePromotionQuery } from 'generated/sanity-graphql';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';

import { buildEntitiesWithBadgeMap } from './utils';

interface IUseAcceleratorBadgesOnMenuProps {
  menuData: MenuObject[];
  skipExposureLogging: boolean;
}

export type AcceleratorBadgeType = 'pointsMultiplier' | 'bonusPoints';

export interface IUseAcceleratorBadgesOnMenu {
  badges: Set<string>;
  pointsMultiplier: number;
  bonusPoints: number;
}

export const useAcceleratorBadgesOnMenu = ({
  menuData,
  skipExposureLogging = false,
}: IUseAcceleratorBadgesOnMenuProps): IUseAcceleratorBadgesOnMenu => {
  // Log exposure
  useAcceleratorBadgesOnMenuExperiment(skipExposureLogging);

  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser, loading } = useLoyaltyUser();

  const promotionId = loyaltyUser?.bestPromotion?.sanityId ?? '';

  const skip = !loyaltyEnabled || loading || !promotionId;

  const { data } = useSystemwidePromotionQuery({
    variables: {
      id: promotionId,
    },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const cartRequirement: Set<string> = useMemo(() => {
    const cartRequirements =
      data?.SystemwidePromotion?.systemwidePromotion?.find(
        req => req?.__typename === 'CartRequirement'
      ) || {};

    if ('cartRequirement' in cartRequirements) {
      return new Set(
        cartRequirements.cartRequirement?.map(
          cartItemRequirement => cartItemRequirement?.item?._id ?? ''
        ) ?? []
      );
    }
    return new Set([]);
  }, [data?.SystemwidePromotion?.systemwidePromotion]);

  const badges = useMemo(() => {
    return buildEntitiesWithBadgeMap(menuData, cartRequirement);
  }, [cartRequirement, menuData]);

  const pointsMultiplier = data?.SystemwidePromotion?.pointsMultiplier ?? 0;
  const bonusPoints = data?.SystemwidePromotion?.bonusPoints ?? 0;

  return {
    badges,
    pointsMultiplier,
    bonusPoints,
  };
};
